import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNotification } from './NotificationContext';

const OTPContext = createContext(null);

export const useOTP = () => {
  return useContext(OTPContext);
};

export const OTPProvider = ({ children }) => {
  const [isOTPRequired, setIsOTPRequired] = useState(false);
  const [otpEndpoint, setOTPEndpoint] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { showNotification } = useNotification();

  const initiateOTPFlow = useCallback(async (phone) => {
    setLoading(true);
    try {
      const response = await fetch('/api/whatsapp/initiate-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ phoneNumber: phone })
      });

      const data = await response.json();

      if (data.success) {
        setPhoneNumber(phone);
        setIsOTPSent(true);
        setIsOTPRequired(true);
        
        showNotification({
          message: 'Código OTP enviado a WhatsApp',
          severity: 'success'
        });

        return { success: true };
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      showNotification({
        message: error.message || 'Error enviando código OTP',
        severity: 'error'
      });
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const verifyOTP = useCallback(async (otp) => {
    setIsVerifying(true);
    try {
      const response = await fetch('/api/whatsapp/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          phoneNumber,
          otp 
        })
      });

      const data = await response.json();

      if (data.success) {
        setIsOTPRequired(false);
        setIsOTPSent(false);
        setPhoneNumber('');
        
        showNotification({
          message: 'WhatsApp conectado exitosamente',
          severity: 'success'
        });

        return { success: true };
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      showNotification({
        message: error.message || 'Error verificando código OTP',
        severity: 'error'
      });
      return { success: false, error: error.message };
    } finally {
      setIsVerifying(false);
    }
  }, [phoneNumber, showNotification]);

  const resetOTPFlow = useCallback(() => {
    setIsOTPRequired(false);
    setIsOTPSent(false);
    setPhoneNumber('');
    setIsVerifying(false);
    setLoading(false);
  }, []);

  const value = {
    isOTPRequired,
    otpEndpoint,
    phoneNumber,
    isOTPSent,
    isVerifying,
    loading,
    initiateOTPFlow,
    verifyOTP,
    resetOTPFlow,
    setIsOTPRequired,
    setOTPEndpoint
  };

  return (
    <OTPContext.Provider value={value}>
      {children}
    </OTPContext.Provider>
  );
};

export default OTPProvider;